import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Header, Footer, Page } from "@/components/Shell";
import { API, CARD_FRONT } from "@/lib/brand";
import Constellation from "@/components/Constellation";

const stateLabel = (s) => s || "à aligner";

function Cadran({ states }) {
  const s = states || {};
  const rings = [
    { key: "dit", label: "Ce que votre entreprise dit", n: "I" },
    { key: "construit", label: "Ce que votre entreprise construit", n: "II" },
    { key: "permet", label: "Ce que votre entreprise permet", n: "III" },
  ];
  return (
    <div className="grid md:grid-cols-3 gap-8" data-testid="cadran-mb">
      {rings.map((r) => (
        <div key={r.key} className="text-center">
          <div className="relative aspect-square max-w-[240px] mx-auto mb-5">
            <div className="absolute inset-0 rounded-full border border-[color:var(--border-bronze-warm)]" />
            <div className="absolute inset-4 rounded-full border border-[color:var(--border-bronze-warm)] opacity-70" />
            <div className="absolute inset-8 rounded-full border border-[color:var(--border-bronze-warm)] opacity-40" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="font-display italic text-bronze-cuivre text-[22px] text-center px-6">{stateLabel(s[r.key])}</div>
            </div>
          </div>
          <div className="text-[11px] tracking-[0.28em] uppercase text-bronze-cuivre">Plan {r.n}</div>
          <div className="font-display text-[19px] text-ivory mt-1">{r.label}</div>
        </div>
      ))}
    </div>
  );
}

function EditorialBlock({ label, index, title, children }) {
  return (
    <div className="border-t border-[color:var(--border-bronze-warm)] pt-6" data-testid={`block-${label.toLowerCase()}-${index}`}>
      <div className="flex items-baseline gap-4">
        <span className="font-display italic text-bronze-cuivre text-xl">{String(index).padStart(2,"0")}</span>
        <div className="flex-1">
          <div className="text-[11px] tracking-[0.28em] uppercase text-bronze-cuivre">{label}</div>
          <div className="font-display text-[22px] md:text-[24px] text-ivory mt-2">{title}</div>
          <div className="mt-3 text-ivory-2 leading-relaxed text-[16px]">{children}</div>
        </div>
      </div>
    </div>
  );
}

export default function Report() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => { (async () => { const { data } = await axios.get(`${API}/reports/${token}`); setData(data); })(); }, [token]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/reports/${token}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const disp = res.headers["content-disposition"] || "";
      const m = disp.match(/filename="?([^"]+)"?/);
      a.href = url;
      a.download = m ? m[1] : "Rapport_Revelation_Strategique.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } finally { setDownloading(false); }
  };

  if (!data) return (
    <Page tone="dark">
      <Header variant="dark" />
      <div className="max-w-3xl mx-auto px-6 py-24 text-ivory-2">Chargement de votre rapport…</div>
    </Page>
  );

  const { client, report } = data;
  const p = report.personalized;
  const sel = report.selected;

  return (
    <Page tone="dark">
      <Header variant="dark" />

      {/* Introduction éditoriale */}
      <section className="section-dark" data-testid="report-page">
        <div className="max-w-5xl mx-auto px-5 md:px-10 pt-20 pb-16 text-center">
          <div className="overline-dark justify-center inline-flex">Rapport de Révélation Stratégique™</div>
          <h1 className="font-display text-[44px] md:text-[60px] leading-[1.05] text-ivory mt-8">Votre Lecture MB™</h1>
          <div className="rule-cuivre my-10 max-w-xs mx-auto" />
          <div className="font-display italic text-[22px] text-bronze-cuivre">{client.company_name || "Entreprise analysée"}</div>
          {client.leader_name && <div className="text-ivory-2 mt-1">{client.leader_name}</div>}
          <div className="mt-12 flex flex-wrap items-center justify-center gap-6">
            <button onClick={downloadPdf} disabled={downloading} className="btn-primary-on-dark" data-testid="btn-download-pdf">
              {downloading ? "Génération…" : "Télécharger le rapport (PDF)"}
            </button>
            <a href="/contact" className="btn-ghost btn-ghost-ivory" data-testid="btn-book">Présenter mes résultats à la Maison</a>
          </div>
        </div>
      </section>

      {/* Constellation stratégique™ — visualisation symbolique sans chiffres */}
      <section className="section-dark border-t border-white/5" data-testid="constellation-section">
        <div className="max-w-5xl mx-auto px-5 md:px-10 py-20">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <div className="overline-dark justify-center inline-flex">Cartographie de la Méthode MB™</div>
            <h2 className="font-display text-[32px] md:text-[40px] text-ivory mt-6">Votre constellation stratégique™.</h2>
            <p className="mt-5 text-[16px] text-ivory-2 leading-relaxed">
              Une représentation symbolique des six dimensions examinées. Ni score, ni classement — un repère
              visuel pour situer les zones que votre lecture aborde en profondeur.
            </p>
          </div>
          <Constellation />
        </div>
      </section>

      {/* Lecture générale */}
      <section className="section-dark border-t border-white/5">
        <div className="max-w-5xl mx-auto px-5 md:px-10 py-20">
          <div className="overline-dark">01 — Lecture générale</div>
          <h2 className="font-display text-[32px] md:text-[42px] text-ivory mt-4">Votre situation en un coup d'œil.</h2>
          <div className="mt-10 space-y-8 text-[17px] leading-relaxed">
            <div><div className="overline-dark mb-3">Ce que votre entreprise semble vivre</div><p className="text-ivory-2">{p.current_situation}</p></div>
            <div><div className="overline-dark mb-3">Ce qui demande votre attention</div><p className="text-ivory-2">{p.main_attention}</p></div>
            <div className="border-l-2 border-[color:var(--bronze-cuivre)] pl-6">
              <div className="overline-dark mb-3">Votre priorité absolue</div>
              <div className="font-display text-[26px] text-ivory">{sel.priority.title}</div>
              <p className="mt-3 text-ivory-2">{p.priority_text}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Dimensions examinées — Cadran MB */}
      <section className="section-dark border-t border-white/5">
        <div className="max-w-5xl mx-auto px-5 md:px-10 py-20">
          <div className="overline-dark">02 — Dimensions examinées</div>
          <h2 className="font-display text-[32px] md:text-[42px] text-ivory mt-4 mb-12">Les trois plans de lecture MB™.</h2>
          <Cadran states={sel.plan_states} />
        </div>
      </section>

      {/* Fondations solides */}
      <section className="section-dark border-t border-white/5">
        <div className="max-w-5xl mx-auto px-5 md:px-10 py-20">
          <div className="overline-dark">03 — Fondations solides</div>
          <h2 className="font-display text-[32px] md:text-[42px] text-ivory mt-4 mb-10">Vos Forces Structurantes™.</h2>
          <div className="space-y-10">
            {p.forces.map((r, i) => <EditorialBlock key={`force-${r.title}`} label="Force" index={i + 1} title={r.title}>{r.text}</EditorialBlock>)}
          </div>
        </div>
      </section>

      {/* Tensions observées */}
      <section className="section-dark border-t border-white/5">
        <div className="max-w-5xl mx-auto px-5 md:px-10 py-20">
          <div className="overline-dark">04 — Tensions observées</div>
          <h2 className="font-display text-[32px] md:text-[42px] text-ivory mt-4 mb-10">Vos Angles Morts™.</h2>
          <div className="space-y-10">
            {p.blind_spots.map((r, i) => <EditorialBlock key={`blind-${r.title}`} label="Angle mort" index={i + 1} title={r.title}>{r.text}</EditorialBlock>)}
          </div>
        </div>
      </section>

      {/* Points de clarification */}
      <section className="section-dark border-t border-white/5">
        <div className="max-w-5xl mx-auto px-5 md:px-10 py-20">
          <div className="overline-dark">05 — Points de clarification</div>
          <h2 className="font-display text-[32px] md:text-[42px] text-ivory mt-4 mb-10">Vos Révélations Stratégiques™.</h2>
          <div className="space-y-10">
            {p.revelations.map((r, i) => <EditorialBlock key={`rev-${r.title}`} label="Révélation" index={i + 1} title={r.title}>{r.text}</EditorialBlock>)}
          </div>
        </div>
      </section>

      {/* Leviers */}
      <section className="section-dark border-t border-white/5">
        <div className="max-w-5xl mx-auto px-5 md:px-10 py-20">
          <div className="overline-dark">06 — Questions de direction</div>
          <h2 className="font-display text-[32px] md:text-[42px] text-ivory mt-4 mb-10">Vos Leviers de Croissance™.</h2>
          <div className="space-y-10">
            {p.levers.map((r, i) => <EditorialBlock key={`lever-${r.title}`} label="Levier" index={i + 1} title={r.title}>{r.text}</EditorialBlock>)}
          </div>
        </div>
      </section>

      {/* Prochaine étape */}
      <section className="section-dark border-t border-white/5" data-testid="next-decision">
        <div className="max-w-5xl mx-auto px-5 md:px-10 py-20">
          <div className="overline-dark">07 — Prochaine étape</div>
          <h2 className="font-display text-[32px] md:text-[42px] text-ivory mt-4">Votre prochaine décision.</h2>
          <p className="mt-6 text-[17px] text-ivory-2 leading-relaxed">{p.next_decision_text}</p>
          <ol className="mt-8 space-y-3 text-ivory-2 text-[16px] leading-relaxed">
            <li className="flex gap-4"><span className="font-display italic text-bronze-cuivre">01</span><span>Relire la priorité absolue.</span></li>
            <li className="flex gap-4"><span className="font-display italic text-bronze-cuivre">02</span><span>Choisir une seule action à poser dans les 30 prochains jours.</span></li>
            <li className="flex gap-4"><span className="font-display italic text-bronze-cuivre">03</span><span>Identifier qui doit être impliqué.</span></li>
            <li className="flex gap-4"><span className="font-display italic text-bronze-cuivre">04</span><span>Retirer ce qui crée de la confusion.</span></li>
            <li className="flex gap-4"><span className="font-display italic text-bronze-cuivre">05</span><span>Revenir au rapport dans un mois pour vérifier ce qui a changé.</span></li>
          </ol>
        </div>
      </section>

      {/* Section finale */}
      <section className="section-dark border-t border-white/5" data-testid="next-step-card">
        <div className="max-w-4xl mx-auto px-5 md:px-10 py-24 text-center">
          <div className="overline-dark justify-center inline-flex">Pour aller plus loin</div>
          <h2 className="font-display text-[36px] md:text-[48px] text-ivory leading-tight mt-6">Présenter vos résultats à la Maison.</h2>
          <p className="mt-6 text-[18px] text-ivory-2 leading-relaxed max-w-2xl mx-auto">
            Le Révélateur™ propose une première lecture de votre situation. Certaines conclusions peuvent demander une interprétation plus approfondie afin d'éclairer les décisions qui engagent la prochaine étape de votre organisation.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-6">
            <a href="/contact" className="btn-primary-on-dark">Présenter mes résultats à la Maison</a>
            <a href="https://maisonbeaudette.com" target="_blank" rel="noopener noreferrer" className="btn-ghost btn-ghost-ivory">Découvrir Maison Beaudette™</a>
          </div>
          <img src={CARD_FRONT} alt="Carte Maison Beaudette" className="mt-14 mx-auto max-w-md w-full opacity-95 border border-white/10" />
        </div>
      </section>

      <Footer />
    </Page>
  );
}
